export const EASE_OUT_QUINT = "cubic-bezier(0.23, 1, 0.32, 1)";

/**
 * @class Layer
 */
export default class Layer {
  /**
   * @param {Spinner} spinner
   * @param {HTMLElement} node - The host element that wraps the layer
   * @param {HTMLElement} [subjectNode] - Optional subject element (defaults to
   * first child)
   */
  constructor(spinner, node, subjectNode) {
    this.spinner = spinner;
    this.host = node;
    this.subject = null;
    this._available = true;
    this._smoothTransform = this.host.classList.contains("motion");
    this._visibility = this.host.style.display !== "none";
    this._activeAnimation = null;
    this.subjectChildren = {};
    this.animations = new Map();

    this.setSubject(subjectNode ?? this.host.firstElementChild);
  }

  /** @type {string} Base URL for images from the spinner */
  get imagesBaseUrl() {
    return this.spinner.imagesBaseUrl;
  }

  get available() {
    return this._available;
  }
  set available(flag) {
    this._available = Boolean(flag);
    this.visibility = this._visibility;
  }

  get opacity() {
    return this.host.style.opacity;
  }

  get visibility() {
    return this._available ? this._visibility : false;
  }
  set visibility(value) {
    this._visibility = Boolean(value);
    this.host.style.display =
      this._available && this._visibility ? null : "none";
  }

  get subjectOpacity() {
    return this.subject.style.opacity;
  }

  set subjectOpacity(value) {
    this.subject.style.opacity = value;
  }

  get smoothTransform() {
    return this._smoothTransform;
  }
  set smoothTransform(value) {
    this._smoothTransform = Boolean(value);
    this.host.classList.toggle("motion", this._smoothTransform);
  }

  get disabled() {
    return this.host.classList.contains("disabled");
  }
  set disabled(value) {
    this.host.classList.toggle("disabled", Boolean(value));
  }

  get cssText() {
    return this.subject.style.cssText;
  }
  set cssText(value) {
    this.subject.style.cssText = value;
  }

  getElmById(id) {
    return this.host.querySelector(`#${id}`);
  }

  set renderFast(value) {
    if (value) {
      this.subject.style.imageRendering = "pixelated";
      this.subject.style.willChange = "transform";
    } else {
      this.subject.style.imageRendering = "";
      this.subject.style.willChange = "";
    }
  }

  get renderFast() {
    return this.subject.style.imageRendering === "pixelated";
  }

  /**
   * Set the main content element (subject) of this layer
   * @param {HTMLElement} subject
   */
  setSubject(subject) {
    if (this.subject && this.subject.parentNode === this.host) {
      this.host.removeChild(this.subject);
    }

    this.host.insertBefore(subject, this.host.firstChild);
    this.subject = subject;

    this.subjectChildren = {};
    // child nodes that have id
    for (const child of this.subject.children) {
      if (child.id) {
        this.subjectChildren[child.id] = child;
      }
    }
  }

  /**
   * @param {string} event
   * @param {EventListener} callback
   * @param {boolean|AddEventListenerOptions} [options]
   */
  on(event, callback, options) {
    this.host.addEventListener(event, callback, options);
  }

  /**
   * @param {string} event
   * @param {EventListener} callback
   * @param {boolean|EventListenerOptions} [options]
   */
  off(event, callback, options) {
    this.host.removeEventListener(event, callback, options);
  }

  /**
   * @param {HTMLElement} element
   * @returns {HTMLElement}
   */
  appendToSubject(element) {
    return this.subject.appendChild(element);
  }

  /**
   * Append an element to a specific named child of the subject
   * @param {HTMLElement} element
   * @param {string} childId
   * @returns {HTMLElement}
   */
  appendTo(element, childId) {
    const target = this.subjectChildren[childId];
    if (!target) {
      throw new Error(`No child with id "${childId}" found in layer subject`);
    }
    return target.appendChild(element);
  }

  /**
   * Animate the subject with Web Animation API
   * @param {KeyframeAnimationOptions['keyframes']} css -
   * @param {() => void} [onDone] - Callback when animation completes
   * @returns {Animation|null}
   */
  animate(
    css,
    onDone = () => {
      // Default no-op
    },
  ) {
    if (!this._smoothTransform || !this._visibility) {
      if (this._activeAnimation) {
        this._activeAnimation.cancel();
      }

      Object.assign(this.subject.style, css);
      onDone();

      return null;
    }

    if (this._activeAnimation) {
      this._activeAnimation.commitStyles();
      this._activeAnimation.cancel();
    }

    this._activeAnimation = this.subject.animate(css, {
      duration: 100,
      fill: "both",
      easing: "linear",
    });

    this._activeAnimation.finished
      .then(() => {
        this._activeAnimation.commitStyles();
        this._activeAnimation.cancel();
        onDone();
      })
      .catch(() => {
        // Animation was canceled
      });

    return this._activeAnimation;
  }

  /**
   * @typedef AnimationOptions
   * @param {number} duration
   * @param {number} [delay]
   * @param {string} [easing]
   * @param {boolean} [override]
   * @param {string} [fill]
   */

  /**
   *
   * @param {HTMLElement} node
   * @param {object[]} keyframes
   * @param {AnalyserOptions} options
   * @param {Function} [onStart]
   * @param {Function} [onComplete]
   */
  animateNode(node, keyframes, options, onStart, onComplete) {
    if (this.animations.has(node)) {
      const ca = this.animations.get(node);

      if (options.override) {
        ca.commitStyles();
        ca.cancel();
      } else {
        return;
      }
    }

    onStart?.();
    const animation = node.animate(keyframes, {
      fill: "both",
      ...options,
    });

    this.animations.set(node, animation);
    animation.finished
      .then(() => {
        onComplete?.();
        animation.commitStyles();
        animation.cancel();
        this.animations.delete(node);
      })
      .catch(() => {
        // Animation was canceled
        animation.cancel();
        this.animations.delete(node);
      });
  }
}
